import { useEffect, useState } from "react";
import { ArrowRight, ArrowDown, Megaphone, Globe, Database, Wallet, Workflow, Users, Target, Rocket, Mail, Ear, ListChecks, Puzzle, Hammer, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { useLang, tx, SERVICE_KEYS, CONTACT_EMAIL } from "./i18n";
import { Reveal, Eyebrow, CoreMark, useSeo } from "./bits";
import { CaseCard } from "./CaseCard";
import { ContactForm } from "./ContactForm";

const ICONS = { marketing: Megaphone, siti: Globe, gestionali: Database, finance: Wallet, processi: Workflow, personale: Users, advertising: Target, partner: Rocket };
const STEP_ICONS = [Ear, ListChecks, Puzzle, Hammer, TrendingUp];
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const Hero = ({ testi }) => {
  const { t, lang } = useLang();
  return (
    <section className="relative bg-[#7CC6FA] pt-[calc(72px+env(safe-area-inset-top))] overflow-hidden" data-testid="site-hero">
      <div className="absolute inset-0 site-grain pointer-events-none" aria-hidden />
      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-16 lg:pb-24 grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
        <div>
          <Reveal><div className="inline-flex items-center gap-2 rounded-full bg-white/60 px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0A1128]"><span className="h-2 w-2 rounded-full bg-[#E8173A] animate-pulse" />{t.hero.eyebrow}</div></Reveal>
          <Reveal delay={80}><h1 className="mt-6 font-display font-black tracking-[-0.03em] leading-[0.98] text-[2.6rem] sm:text-6xl lg:text-7xl text-[#0A1128]" data-testid="site-hero-title">{tx(testi?.hero_title, lang)}</h1></Reveal>
          <Reveal delay={160}><p className="mt-6 max-w-xl text-base sm:text-lg text-[#0A1128]/80 leading-relaxed" data-testid="site-hero-sub">{tx(testi?.hero_sub, lang)}</p></Reveal>
          <Reveal delay={240} className="mt-8 flex flex-col sm:flex-row gap-3">
            <button onClick={() => scrollTo("casi")} className="h-14 px-7 rounded-full bg-[#0A1128] text-white font-bold inline-flex items-center justify-center gap-2 hover:bg-[#E8173A] transition-colors" data-testid="site-hero-cta-cases">{t.hero.cta1}<ArrowRight className="h-5 w-5" /></button>
            <button onClick={() => scrollTo("contatti")} className="h-14 px-7 rounded-full bg-white text-[#0A1128] font-bold inline-flex items-center justify-center gap-2 hover:bg-[#EAF6FF] transition-colors" data-testid="site-hero-cta-contact">{t.hero.cta2}</button>
          </Reveal>
          <Reveal delay={320}><div className="mt-10 text-sm font-bold text-[#0A1128]"><CoreMark className="text-lg" /> — the <span className="text-[#E8173A]">heart</span> of your business</div></Reveal>
        </div>
        <Reveal delay={150} className="relative">
          <div className="relative aspect-[4/3] rounded-[36px] overflow-hidden shadow-[0_40px_90px_rgba(10,17,40,0.25)]">
            <img src="/site/hero.webp" alt="" className="h-full w-full object-cover site-float" fetchPriority="high" />
          </div>
          <div className="absolute -bottom-5 left-5 sm:-left-6 rounded-2xl bg-[#0A1128] text-white px-5 py-4 shadow-xl max-w-[260px]">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF5A72]">{t.metodo.eyebrow}</div>
            <div className="mt-1 text-sm font-semibold">{t.metodo.steps.map((s) => s[0]).join(" → ")}</div>
          </div>
        </Reveal>
      </div>
      <button onClick={() => scrollTo("about")} className="hidden lg:flex absolute bottom-6 left-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-white/60 items-center justify-center" aria-label="Scroll"><ArrowDown className="h-5 w-5 animate-bounce" /></button>
    </section>
  );
};

const About = ({ testi }) => {
  const { t, lang } = useLang();
  return (
    <section id="about" className="scroll-mt-20 bg-[#0A1128] text-white" data-testid="site-about">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20">
        <div>
          <Reveal><Eyebrow light>{t.about.eyebrow}</Eyebrow><h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{t.about.title}</h2></Reveal>
          <Reveal delay={120} className="mt-10">
            <blockquote className="relative border-l-4 border-[#E8173A] pl-6 font-display font-extrabold text-2xl sm:text-3xl leading-snug text-white" data-testid="site-quote">
              <span className="text-[#E8173A]">“</span>{tx(testi?.quote, lang)}<span className="text-[#E8173A]">”</span>
            </blockquote>
            <div className="mt-6 text-sm text-white/70"><span className="font-display font-black text-white">core<span className="text-[#E8173A]">.</span></span> — the heart of your business</div>
          </Reveal>
        </div>
        <div className="space-y-6 text-base sm:text-lg leading-relaxed text-white/80">
          {["about_p1", "about_p2", "about_p3"].map((k, i) => <Reveal key={k} delay={i * 100}><p data-testid={`site-${k}`}>{tx(testi?.[k], lang)}</p></Reveal>)}
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const { t } = useLang();
  return (
    <section id="servizi" className="scroll-mt-20" data-testid="site-services">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <Reveal className="max-w-2xl"><Eyebrow>{t.servizi.eyebrow}</Eyebrow><h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{t.servizi.title}</h2><p className="mt-4 text-slate-600 text-base sm:text-lg">{t.servizi.sub}</p></Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICE_KEYS.map((k, i) => {
            const Icon = ICONS[k];
            return (
              <Reveal key={k} delay={(i % 4) * 70} className="group rounded-[24px] bg-white p-6 border border-[#7CC6FA]/50 hover:border-[#0A1128] hover:-translate-y-1 transition-[transform,border-color] duration-300" data-testid={`site-service-${k}`}>
                <div className="h-12 w-12 rounded-2xl bg-[#7CC6FA]/35 text-[#0A1128] flex items-center justify-center group-hover:bg-[#E8173A] group-hover:text-white transition-colors"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-5 font-display text-lg font-extrabold leading-tight">{t.servizi.items[k][0]}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.servizi.items[k][1]}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Method = () => {
  const { t } = useLang();
  return (
    <section id="metodo" className="scroll-mt-20 bg-[#7CC6FA]" data-testid="site-method">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <Reveal className="max-w-2xl"><Eyebrow>{t.metodo.eyebrow}</Eyebrow><h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{t.metodo.title}</h2></Reveal>
        <ol className="mt-12 grid gap-4 lg:grid-cols-5 relative">
          <div className="hidden lg:block absolute top-8 left-8 right-8 h-0.5 bg-[#0A1128]/15" aria-hidden />
          {t.metodo.steps.map(([title, desc], i) => {
            const Icon = STEP_ICONS[i];
            return (
              <Reveal as="li" key={title} delay={i * 90} className="relative flex lg:block gap-4 rounded-[24px] bg-white/70 lg:bg-transparent p-5 lg:p-0" data-testid={`site-step-${i}`}>
                <div className="relative z-10 h-16 w-16 shrink-0 rounded-full bg-[#0A1128] text-white flex items-center justify-center shadow-lg"><Icon className="h-7 w-7" /><span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-[#E8173A] text-[11px] font-black flex items-center justify-center">{i + 1}</span></div>
                <div className="lg:mt-5 lg:pr-4">
                  <h3 className="font-display text-xl font-black">{title}</h3>
                  <p className="mt-1.5 text-sm text-[#0A1128]/75 leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

const Cases = ({ cases }) => {
  const { t } = useLang();
  return (
    <section id="casi" className="scroll-mt-20" data-testid="site-cases">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <Reveal className="max-w-2xl"><Eyebrow>{t.casi.eyebrow}</Eyebrow><h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{t.casi.title}</h2><p className="mt-4 text-slate-600 text-base sm:text-lg">{t.casi.sub}</p></Reveal>
        {cases && !cases.length && <p className="mt-10 text-slate-500" data-testid="site-cases-empty">{t.casi.empty}</p>}
        <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {(cases || []).map((c, i) => <Reveal key={c.id} delay={(i % 4) * 90} className="h-full"><CaseCard c={c} /></Reveal>)}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const { t } = useLang();
  return (
    <section id="contatti" className="scroll-mt-20 bg-[#7CC6FA]" data-testid="site-contact">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16">
        <Reveal>
          <Eyebrow>{t.contatti.eyebrow}</Eyebrow>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{t.contatti.title}</h2>
          <p className="mt-4 text-[#0A1128]/80 text-base sm:text-lg">{t.contatti.sub}</p>
          <div className="mt-8 text-sm font-bold text-[#0A1128]/70">{t.contatti.scrivi}</div>
          <a href={`mailto:${CONTACT_EMAIL}`} className="mt-2 inline-flex items-center gap-2 font-display text-xl sm:text-2xl font-black text-[#0A1128] hover:text-[#E8173A] transition-colors break-all" data-testid="site-contact-email"><Mail className="h-6 w-6 shrink-0" />{CONTACT_EMAIL}</a>
        </Reveal>
        <Reveal delay={120}><ContactForm /></Reveal>
      </div>
    </section>
  );
};

export default function HomePage() {
  const { t } = useLang();
  const [testi, setTesti] = useState(null);
  const [cases, setCases] = useState(null);
  useSeo(t.seo.title, t.seo.desc);
  useEffect(() => {
    api.get("/public/testi").then((r) => setTesti(r.data)).catch(() => setTesti({}));
    api.get("/public/case-history").then((r) => setCases(r.data)).catch(() => setCases([]));
  }, []);
  return (
    <div data-testid="site-home">
      <Hero testi={testi} />
      <About testi={testi} />
      <Services />
      <Method />
      <Cases cases={cases} />
      <Contact />
    </div>
  );
}
